import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Avatar,
  TextField,
  MenuItem,
  Button,
  FormControl,
  Select,
  IconButton,
  FormHelperText,
  Autocomplete,
  Chip,
  Stack,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import GroupIcon from "@mui/icons-material/Group";
import Add from "@mui/icons-material/Add";
import InputAdornment from "@mui/material/InputAdornment";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import files from "src/utils/constants";
import { axiosInstance } from "src/network/adapter";
import { ApiEndPoints } from "src/network/endpoints";
import { useQuery, useMutation, useQueryClient } from "react-query";
import AllSelectedLocationsDialog from "./team-member-linking-form-page/dialogs/allLocationsDialog";
import PermissionsDialog from "./team-member-linking-form-page/dialogs/permissionsDialog";
import SelectedLocationsDialog from "./team-member-linking-form-page/dialogs/selectedLocationsDialog";
import WorkHoursDialog from "./team-member-linking-form-page/dialogs/workingHoursDialog";
import SelectedManagersDialog from "./team-member-linking-form-page/dialogs/selectedMangersDialog";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";

// Optimized mutation hook for adding new departments
const useAddMutation = (endpoint, queryKey) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (name) => {
      const response = await axiosInstance.post(endpoint, { name });
      return response.data;
    },
    {
      onSuccess: () => queryClient.invalidateQueries([queryKey]),
      onError: (error) => console.error(`Error adding ${queryKey}:`, error),
    }
  );
};

function TeamMemberLinkingForm({ initialData = {}, onBack, onSuccess, showBackButton = true }) {
  const { t } = useTranslation("dashboard");
  const queryClient = useQueryClient();
  const selectedBusinessId = useSelector((state) => state.auth.selectedBusinessId);
  
  // Validation schema (needs t() for error messages)
  const schema = yup.object({
    memberId: yup.string().required(t("teamMemberLinkingFormMemberIdRequired")),
    department: yup.string().required(t("teamMemberLinkingFormDepartmentRequired")),
    designation: yup.string().required(t("teamMemberLinkingFormDesignationRequired")),
    systemRole: yup
      .object()
      .nullable()
      .required(t("teamMemberLinkingFormSystemRoleRequired"))
      .test(
        "not-default",
        t("teamMemberLinkingFormSystemRoleValid"),
        (value) => value && value.roleName !== "default"
      ),
    location: yup
      .array()
      .of(
        yup.object({
          _id: yup.string().required(),
          businessLocationName: yup.string().required(),
          permissions: yup
            .object()
            .nullable()
            .test(
              "permissions-required",
              t("teamMemberLinkingFormPermissionsRequired"),
              (value) => value && Object.keys(value).length > 0
            )
            .required(t("teamMemberLinkingFormPermissionsRequired")),
          workingHours: yup
            .object()
            .nullable()
            .test(
              "working-hours-required",
              t("teamMemberLinkingFormWorkingHoursRequired"),
              (value) => value && Object.keys(value).length > 0
            )
            .required(t("teamMemberLinkingFormWorkingHoursRequired")),
        })
      )
      .min(1, t("teamMemberLinkingFormLocationRequired")),
    manager: yup
      .array()
      .of(
        yup.object().shape({
          name: yup.string().required(t("teamMemberLinkingFormManagerNameRequired")),
        })
      )
      .nullable(),
  });
  
  // Check if this is edit mode
  const isEditMode = initialData?.isEdit || false;
  const employeeData = initialData || {};
  const [isLoadingData, setIsLoadingData] = useState(isEditMode);

  // Dialog states
  const [openSelectedLocationFirstDialog, setSelectedLocationFirstDialog] =
    useState(false);
  const [openSelectedManagersDialog, setSelectedManagersDialog] =
    useState(false);
  const [openSelectedLocationDialog, setSelectedLocationDialog] =
    useState(false);
  const [openPermissionDialog, setOpenPermissionDialog] = useState(false);
  const [openSelectWorkingHourDialog, setOpenSelectWorkingHourDialog] =
    useState(false);
  const [selectedLocationIndex, setSelectedLocationIndex] = useState(null);

  // Form setup with default values based on edit mode
  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    watch,
    reset,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      memberId: "",
      department: "",
      designation: "",
      systemRole: { roleName: "default" },
      location: [],
      manager: [],
    },
  });

  const watch_location = watch("location");
  const watch_systemRole = watch("systemRole");
  const watch_manager = watch("manager");

  // Memoized location IDs to prevent unnecessary re-renders
  const selectedLocationIds = useMemo(() => {
    return watch_location?.map((loc) => loc._id) || [];
  }, [watch_location]);

  // Create stable location IDs string for query key
  const locationIdsKey = useMemo(() => {
    return selectedLocationIds.sort().join(",");
  }, [selectedLocationIds]);

  // Dialog handlers
  const handleDialogToggle = useCallback((setter) => {
    return () => setter((prev) => !prev);
  }, []);

  const hanldeOpenSelectedLocationFirstDialog = handleDialogToggle(
    setSelectedLocationFirstDialog
  );
  const hanldeOpenSelectedManagersDialog = handleDialogToggle(
    setSelectedManagersDialog
  );
  const hanldeOpenSelectedLocationDialog = handleDialogToggle(
    setSelectedLocationDialog
  );
  const hanldeOpenPermissionDialog = handleDialogToggle(
    setOpenPermissionDialog
  );
  const hanldeOpenSelectWorkingHourDialog = handleDialogToggle(
    setOpenSelectWorkingHourDialog
  );

  // Fetch system roles (only once)
  const { data: systemRoleList } = useQuery(
    ["system-role-list"],
    async () => {
      const res = await axiosInstance.get(
        `${ApiEndPoints.TEAM_LINKING.get_system_roles}`
      );
      return res?.data?.data?.roles;
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  // Fetch business locations (only once)
  const { data: businessLocationList } = useQuery(
    ["business-location-list", selectedBusinessId],
    async () => {
      const res = await axiosInstance.get(
        `${ApiEndPoints.TEAM_LINKING.get_business_locations(
          selectedBusinessId
        )}`
      );
      return res?.data?.data;
    },
    {
      enabled: !!selectedBusinessId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  // Optimized managers query with proper caching and error handling
  const {
    data: managersList,
    isLoading: managersLoading,
    error: managersError,
  } = useQuery(
    ["managers-list", locationIdsKey],
    async () => {
      if (selectedLocationIds.length === 0) {
        return [];
      }

      const payload = {
        locationIds: selectedLocationIds,
      };

      const res = await axiosInstance.post(
        `${ApiEndPoints.TEAM_LINKING.get_managers(selectedBusinessId)}`,
        payload
      );
      return res?.data?.data?.employees || [];
    },
    {
      enabled: !!selectedBusinessId && selectedLocationIds.length > 0,
      staleTime: 2 * 60 * 1000, // 2 minutes
      cacheTime: 5 * 60 * 1000, // 5 minutes
      keepPreviousData: true, // Keep previous data while fetching new
      refetchOnWindowFocus: false, // Prevent refetch on tab switch
      refetchOnMount: false, // Prevent refetch on component remount
    }
  );

  // Fetch departments
  const { data: departmentsData } = useQuery(
    ["department"],
    async ({ signal }) => {
      const res = await axiosInstance.get(ApiEndPoints.COMMON.department, {
        signal,
      });
      return res.data.data;
    },
    { refetchOnWindowFocus: false, cacheTime: 30 * 60 * 1000 }
  );

  // Add department mutation
  const addDepartmentMutation = useAddMutation(
    ApiEndPoints.COMMON.department,
    "department"
  );

  // Populate form with existing data when editing
  useEffect(() => {
    if (isEditMode && employeeData && Object.keys(employeeData).length > 0) {
      setIsLoadingData(true);
      try {
        // Map the API response structure to form fields
        const mappedData = {
          memberId: employeeData.teamMemberId || "",
          department: employeeData.department || "",
          designation: employeeData.designation || "",
          systemRole: { roleName: "default" }, // Will be updated when systemRoleList is loaded
          location: [],
          manager: [],
        };

        // Map locations with permissions and working hours
        if (employeeData.locationSchedules && employeeData.locationPermissions) {
          const mappedLocations = employeeData.locationSchedules.map((schedule, index) => {
            const permissions = employeeData.locationPermissions[index]?.permissions || {};
            
            // Map working hours based on schedule type
            let workingHours = null;
            if (schedule.scheduleType === "standard") {
              workingHours = {
                scheduleType: "standard",
                workingDays: schedule.workingDays || [],
                standardWorkHours: {
                  startTime: schedule.standardWorkHours?.startTime || "09:00 am",
                  endTime: schedule.standardWorkHours?.endTime || "05:00 pm"
                },
                isActive: schedule.isActive || true
              };
            } else if (schedule.scheduleType === "custom") {
              workingHours = {
                scheduleType: "custom",
                workingDays: schedule.workingDays || [],
                customSchedule: schedule.customSchedule || {},
                isActive: schedule.isActive || true
              };
            } else if (schedule.scheduleType === "advanced") {
              workingHours = {
                scheduleType: "advanced",
                workingDays: schedule.workingDays || [],
                advancedSettings: {
                  startDate: schedule.advancedSettings?.startDate || new Date().toISOString(),
                  startEndTime: {
                    startTime: schedule.advancedSettings?.startEndTime?.startTime || "09:00 am",
                    endTime: schedule.advancedSettings?.startEndTime?.endTime || "05:00 pm"
                  },
                  customRecurrence: {
                    repeatEvery: schedule.advancedSettings?.customRecurrence?.repeatEvery || 1,
                    repeatUnit: schedule.advancedSettings?.customRecurrence?.repeatUnit || "weeks",
                    repeatOn: schedule.advancedSettings?.customRecurrence?.repeatOn || [],
                    endsOn: schedule.advancedSettings?.customRecurrence?.endsOn || "never"
                  }
                },
                isActive: schedule.isActive || true
              };
            }

            return {
              _id: schedule.locationId,
              businessLocationName: schedule.locationName,
              permissions: permissions,
              workingHours: workingHours,
              systemRole: mappedData.systemRole
            };
          });
          
          mappedData.location = mappedLocations;
        }

        // Map managers
        if (employeeData.teamMemberManger && Array.isArray(employeeData.teamMemberManger)) {
          mappedData.manager = employeeData.teamMemberManger.map(managerId => ({
            id: managerId,
            name: t("teamMemberLinkingFormLoading") // Will be updated when managers are fetched
          }));
        }

        // Reset form with mapped data
        reset(mappedData);
        setIsLoadingData(false);
      } catch (error) {
        console.error("Error mapping employee data:", error);
        setIsLoadingData(false);
        toast.error(t("teamMemberLinkingFormFailedToLoadEmployeeData"));
      }
    }
  }, [isEditMode, employeeData, reset]);

  // Update system role when systemRoleList is loaded in edit mode
  useEffect(() => {
    if (isEditMode && employeeData?.systemRole && systemRoleList) {
      const foundRole = systemRoleList.find(role => role._id === employeeData.systemRole);
      if (foundRole) {
        setValue("systemRole", foundRole);
      }
    }
  }, [isEditMode, employeeData?.systemRole, systemRoleList, setValue]);

  // Update managers list when editing to show existing managers
  useEffect(() => {
    if (isEditMode && employeeData.teamMemberManger && managersList) {
      const existingManagers = employeeData.teamMemberManger.map(managerId => {
        const manager = managersList.find(m => m.userId?._id === managerId);
        return {
          id: managerId,
          name: manager ? manager.userId?.fullName || t("teamMemberLinkingFormUnnamed") : t("teamMemberLinkingFormUnknownManager")
        };
      });
      
      if (existingManagers.length > 0) {
        setValue("manager", existingManagers);
      }
    }
  }, [isEditMode, employeeData.teamMemberManger, managersList, setValue]);

  // Memoized location selection handler
  const handleLocationSelection = useCallback(
    async (selectedOption, field) => {
      if (selectedOption) {
        const updatedLocation = {
          ...selectedOption,
          permissions: null,
          workingHours: null,
          systemRole: watch_systemRole,
        };

        const newLocations = [...field.value, updatedLocation];
        setValue("location", newLocations);

        const currentManagers = getValues("manager");
        if (currentManagers.length > 0) {
          setValue("manager", []);
        }
      }
    },
    [watch_systemRole, setValue, getValues]
  );

  // Memoized mapped managers to prevent unnecessary recalculations
  const mappedManagers = useMemo(() => {
    return (
      managersList?.map((mgr) => ({
        id: mgr.userId?._id,
        name: mgr.userId?.fullName ?? t("teamMemberLinkingFormUnnamed"),
      })) || []
    );
  }, [managersList, t]);

  // Memoized available manager options
  const availableManagerOptions = useMemo(() => {
    const selectedIds = watch_manager?.map((m) => m.id) || [];
    return mappedManagers.filter((mgr) => !selectedIds.includes(mgr.id));
  }, [mappedManagers, watch_manager]);

  // Memoized filtered business locations
  const availableLocations = useMemo(() => {
    return (businessLocationList?.locations || []).filter(
      (loc) => !watch_location.some((selected) => selected._id === loc._id)
    );
  }, [businessLocationList?.locations, watch_location]);

  // Memoized department options
  const departmentOptions = useMemo(
    () =>
      departmentsData?.departments?.map((dept) => ({
        label: dept.name,
        value: dept.name,
        id: dept._id,
      })) || [],
    [departmentsData]
  );

  // Handle adding new department
  const handleAddNewDepartment = async (value) => {
    if (value?.trim()) {
      try {
        await addDepartmentMutation.mutateAsync(value.trim());
        setValue("department", value.trim());
        toast.success(t("teamMemberLinkingFormDepartmentAdded"));
      } catch (error) {
        console.error("Error adding department:", error);
        toast.error(t("teamMemberLinkingFormFailedToAddDepartment"));
      }
    }
  };

  const submitFormData = async (data) => {
    try {
      const payload = {
        teamMemberId: data.memberId,
        department: data.department,
        designation: data.designation,
        teamMemberManger: data.manager.map((mgr) => mgr.id),
        locationIds: data.location.map((loc) => loc._id),
        systemRole: data.systemRole._id,
        locationPermissions: data.location.map((loc) => ({
          locationId: loc._id,
          permissions: loc.permissions || {},
        })),
        locationSchedules: data.location.map((loc) => {
          const baseSchedule = {
            locationId: loc._id,
            locationName: loc.businessLocationName,
            scheduleType: loc.workingHours?.scheduleType || "standard",
            isActive: loc.workingHours?.isActive || true,
          };

          switch (loc.workingHours?.scheduleType) {
            case "advanced":
              return {
                ...baseSchedule,
                workingDays: loc.workingHours.workingDays || [],
                advancedSettings: {
                  enabled: true,
                  startDate: loc.workingHours.advancedSettings?.startDate || new Date().toISOString(),
                  startEndTime: {
                    startTime: loc.workingHours.advancedSettings?.startEndTime?.startTime || "09:00 am",
                    endTime: loc.workingHours.advancedSettings?.startEndTime?.endTime || "05:00 pm",
                  },
                  customRecurrence: {
                    repeatEvery: loc.workingHours.advancedSettings?.customRecurrence?.repeatEvery || 1,
                    repeatUnit: loc.workingHours.advancedSettings?.customRecurrence?.repeatUnit || "weeks",
                    repeatOn: loc.workingHours.advancedSettings?.customRecurrence?.repeatOn || [],
                    endsOn: loc.workingHours.advancedSettings?.customRecurrence?.endsOn || "never",
                  },
                },
              };

            case "standard":
              return {
                ...baseSchedule,
                workingDays: loc.workingHours.workingDays || ["M", "T", "W", "Th", "F"],
                standardWorkHours: {
                  startTime: loc.workingHours.standardWorkHours?.startTime || "09:00 am",
                  endTime: loc.workingHours.standardWorkHours?.endTime || "05:00 pm",
                },
              };

            case "custom":
              return {
                ...baseSchedule,
                customSchedule: loc.workingHours.customSchedule || {},
              };

            default:
              return {
                ...baseSchedule,
                scheduleType: "standard",
                workingDays: ["M", "T", "W", "Th", "F"],
                standardWorkHours: {
                  startTime: "09:00 am",
                  endTime: "05:00 pm",
                },
              };
          }
        }),
      };

      let response;
      if (isEditMode) {
        response = await axiosInstance.patch(
          ApiEndPoints.TEAM_LINKING.update_team_member(selectedBusinessId, employeeData.employeeId),
          payload
        );
        // Refetch only if status is 200
        if (response.status === 200) {
          queryClient.invalidateQueries(['team-members']);
          queryClient.invalidateQueries(['tab-data-0']); // Refresh linked tab
          toast.success(response.data?.message || t("teamMemberLinkingFormTeamMemberUpdated"));
          if (onSuccess) {
            onSuccess();
          } else if (onBack) {
            onBack();
          }
        }
      } else {
        const userId = initialData._id;
        response = await axiosInstance.post(
          ApiEndPoints.TEAM_LINKING.link_team_member(selectedBusinessId, userId),
          payload
        );
        // Refetch only if status is 200
        if (response.status === 200) {
          queryClient.invalidateQueries(['team-members']);
          queryClient.invalidateQueries(['tab-data-0']); // Refresh linked tab
          toast.success(response.data?.message || t("teamMemberLinkingFormTeamMemberLinked"));
          if (onSuccess) {
            onSuccess();
          } else if (onBack) {
            onBack();
          }
        }
      }
    } catch (error) {
      console.error("Failed to process team member:", error);
      toast.error(error?.response?.data?.message || "something went wrong");
    }
  };

  const onSubmit = async (data) => {
    try {
      const incompleteLocations = data.location.filter(
        loc => !loc.permissions || Object.keys(loc.permissions || {}).length === 0 ||
               !loc.workingHours || Object.keys(loc.workingHours || {}).length === 0
      );
      
      if (incompleteLocations.length > 0) {
        const locationNames = incompleteLocations.map(loc => loc.businessLocationName).join(', ');
        toast.error(t("teamMemberLinkingFormCompleteSetupForLocations", { locations: locationNames }));
        return;
      }

      if (!data.location || data.location.length === 0) {
        toast.error(t("teamMemberLinkingFormSelectAtLeastOneLocation"));
        return;
      }

      await submitFormData(data);
    } catch (error) {
      console.error("Failed to process team member:", error);
      toast.error(error?.response?.data?.message || "something went wrong");
    }
  };

  // Show loading state while data is being populated
  if (isLoadingData) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, width: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
          <CircularProgress size={40} sx={{ color: "#8800F7" }} />
          <Typography sx={{ ml: 2, color: "#666" }}>
            loading employee data...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, width: "100%", maxWidth: "100%" }}>
      {/* Top Bar with Back Button */}
      {showBackButton && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <IconButton
            onClick={onBack}
            size="small"
            sx={{ color: "#222" }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography
            sx={{
              flex: 1,
              textAlign: "center",
              fontWeight: 700,
              fontSize: { xs: 20, sm: 24 },
              textTransform: "lowercase",
            }}
          >
            {isEditMode ? t("teamMemberLinkingEditTeamMember") : t("teamMemberLinkingLinkAsTeamMember")}
          </Typography>
          <Box width={40} />
        </Box>
      )}

      {/* User Info */}
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <Avatar
          src={files(initialData?.profilePic || initialData?.userId?.image?.profilePic, "image")}
          sx={{ width: 56, height: 56, mr: 2 }}
        />
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#222" }}>
            {initialData?.fullName || initialData?.userId?.fullName}
          </Typography>
          <Typography sx={{ color: "#979797", fontSize: 15 }}>
            @{initialData?.userName || initialData?.userId?.userName}
          </Typography>
        </Box>
      </Box>

      {/* Section title */}
      <Typography
        sx={{
          mb: 1.3,
          fontWeight: 700,
          color: "#8800F7",
          fontSize: 17,
        }}
      >
        {t("teamMemberLinkingFormDetail")}
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* team member id */}
        <Typography
          sx={{ fontWeight: 600, color: "#222", fontSize: 15, mb: 0.7 }}
        >
          {t("teamMemberLinkingFormTeamMemberId")}
        </Typography>
        <Controller
          name="memberId"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              placeholder={t("teamMemberLinkingFormEnterIdHere")}
              size="small"
              fullWidth
              error={!!errors.memberId}
              sx={{
                mb: 0.5,
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#fff",
                  borderRadius: 2,
                },
              }}
            />
          )}
        />
        {errors.memberId && (
          <FormHelperText sx={{ color: "#F44336", mb: 2, ml: 1 }}>
            {errors.memberId.message}
          </FormHelperText>
        )}

        {/* department */}
        <Typography
          sx={{ fontWeight: 600, color: "#222", fontSize: 15, mb: 0.7 }}
        >
          {t("teamMemberLinkingFormDepartment")}
        </Typography>
        <Controller
          name="department"
          control={control}
          render={({ field }) => (
            <Autocomplete
              options={departmentOptions}
              getOptionLabel={(option) => option.label}
              value={
                departmentOptions.find(
                  (option) => option.value === field.value
                ) || null
              }
              onChange={(event, newValue) => {
                field.onChange(newValue ? newValue.value : "");
              }}
              freeSolo
              PopperProps={{
                sx: {
                  zIndex: 1800,
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t("teamMemberLinkingFormSearchDepartments")}
                  size="small"
                  error={!!errors.department}
                  sx={{
                    mb: 0.5,
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#E4E2E2",
                      borderRadius: 2,
                      color: field.value ? "#222" : "#979797",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {params.InputProps.endAdornment}
                        {params.inputProps.value &&
                          !departmentOptions.some((option) =>
                            option.label
                              .toLowerCase()
                              .includes(
                                params.inputProps.value.toLowerCase()
                              )
                          ) && (
                            <InputAdornment position="end">
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={
                                  addDepartmentMutation.isLoading ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <Add />
                                  )
                                }
                                onClick={() =>
                                  handleAddNewDepartment(
                                    params.inputProps.value
                                  )
                                }
                                disabled={addDepartmentMutation.isLoading}
                                sx={{
                                  minWidth: "auto",
                                  px: 1,
                                  py: 0.5,
                                  fontSize: "0.75rem",
                                  textTransform: "none",
                                }}
                              >
                                {t("teamMemberLinkingFormAdd")}
                              </Button>
                            </InputAdornment>
                          )}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
        />
        {errors.department && (
          <FormHelperText sx={{ color: "#F44336", mb: 2, ml: 1 }}>
            {errors.department.message}
          </FormHelperText>
        )}

        {/* designation */}
        <Typography
          sx={{ fontWeight: 600, color: "#222", fontSize: 15, mb: 0.7 }}
        >
          {t("teamMemberLinkingFormTeamMemberDesignation")}
        </Typography>
        <Controller
          name="designation"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              placeholder={t("teamMemberLinkingFormEnterHere")}
              size="small"
              fullWidth
              error={!!errors.designation}
              sx={{
                mb: 0.5,
                "& .MuiOutlinedInput-root": {
                  bgcolor: "#fff",
                  borderRadius: 2,
                },
              }}
            />
          )}
        />
        {errors.designation && (
          <FormHelperText sx={{ color: "#F44336", mb: 2, ml: 1 }}>
            {errors.designation.message}
          </FormHelperText>
        )}

        {/* system role */}
        <Typography
          sx={{ fontWeight: 600, color: "#222", fontSize: 15, mb: 0.7 }}
        >
          {t("teamMemberLinkingFormSystemRole")}
        </Typography>
        <Controller
          name="systemRole"
          control={control}
          render={({ field }) => (
            <FormControl
              fullWidth
              size="small"
              error={!!errors.systemRole}
              sx={{ mb: 0.5 }}
            >
              <Select
                value={
                  field.value && field.value.roleName !== "default"
                    ? JSON.stringify(field.value)
                    : ""
                }
                onChange={(e) => {
                  const selectedValue = e.target.value;
                  if (!selectedValue) {
                    field.onChange({ roleName: "default" });
                    return;
                  }

                  const selectedRole = JSON.parse(selectedValue);
                  field.onChange(selectedRole);

                  const currentLocations = getValues("location");
                  if (currentLocations?.length > 0) {
                    const updatedLocations = currentLocations.map((loc) => ({
                      ...loc,
                      systemRole: selectedRole,
                    }));
                    setValue("location", updatedLocations, {
                      shouldValidate: true,
                    });
                  }
                }}
                displayEmpty
                MenuProps={{
                  PaperProps: {
                    sx: {
                      zIndex: 1800,
                    },
                  },
                }}
                sx={{
                  bgcolor: "#E4E2E2",
                  borderRadius: 2,
                  color:
                    field.value && field.value.roleName !== "default"
                      ? "#222"
                      : "#979797",
                  fontWeight: 500,
                }}
              >
                <MenuItem value="" disabled>
                  {t("teamMemberLinkingFormSelectRole")}
                </MenuItem>
                {systemRoleList?.map((role) => (
                  <MenuItem key={role?._id} value={JSON.stringify(role)}>
                    {role?.roleName}
                  </MenuItem>
                ))}
              </Select>
              {errors?.systemRole && (
                <FormHelperText>{errors?.systemRole?.message}</FormHelperText>
              )}
            </FormControl>
          )}
        />

        {/* select location */}
        <Typography
          sx={{ fontWeight: 600, color: "#222", fontSize: 15, mb: 0.7 }}
        >
          {t("teamMemberLinkingFormSelectLocation")}
        </Typography>
        <Controller
          name="location"
          control={control}
          render={({ field }) => (
            <Autocomplete
              options={availableLocations}
              getOptionLabel={(option) => option.businessLocationName || ""}
              value={null}
              onChange={(_, selectedOption) => {
                handleLocationSelection(selectedOption, field);
              }}
              PopperProps={{
                sx: {
                  zIndex: 1800,
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t("teamMemberLinkingFormSelectLocationPlaceholder")}
                  size="small"
                  error={!!errors.location}
                  sx={{
                    mb: 0.5,
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "#E4E2E2",
                      borderRadius: 2,
                      color: "#979797",
                    },
                  }}
                />
              )}
              clearOnBlur
              openOnFocus
              disableClearable
              isOptionEqualToValue={(option, value) =>
                option._id === value._id
              }
            />
          )}
        />

        <Stack direction="row" flexWrap="wrap" gap={1} sx={{ my: 2 }}>
          {(watch_location || []).map((loc) => (
            <Chip
              key={loc._id}
              label={loc.businessLocationName}
              onDelete={() => {
                const updatedLocations = watch_location.filter(
                  (l) => l._id !== loc._id
                );
                setValue("location", updatedLocations);
                setValue("manager", []);
              }}
              sx={{
                bgcolor: "#F1E4FD",
                color: "#8800F7",
                fontWeight: 500,
                "& .MuiChip-deleteIcon": {
                  color: "#8800F7",
                },
              }}
            />
          ))}
        </Stack>

        {/* Count and 'See all' link */}
        {(watch_location || [])?.length > 0 && (
          <Box
            sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
          >
            <Typography sx={{ fontSize: 14, color: "#222" }}>
              {t("teamMemberLinkingFormSelectedCount", { count: (watch_location || [])?.length })}
            </Typography>
            <Box onClick={hanldeOpenSelectedLocationFirstDialog}>
              <Typography
                sx={{
                  fontSize: 14,
                  color: "#596AFF",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {t("teamMemberLinkingFormSeeAll")}
              </Typography>
            </Box>
          </Box>
        )}

        {/* team member manager */}
        <Typography
          sx={{ fontWeight: 600, color: "#222", fontSize: 15, mb: 0.7 }}
        >
          {t("teamMemberLinkingFormTeamMemberManager")}
        </Typography>
        <Controller
          name="manager"
          control={control}
          render={({ field }) => (
            <>
              <Autocomplete
                multiple
                options={availableManagerOptions}
                getOptionLabel={(option) => option.name}
                value={field.value || []}
                onChange={(_, newValue) => field.onChange(newValue)}
                disabled={selectedLocationIds.length === 0}
                loading={managersLoading}
                PopperProps={{
                  sx: {
                    zIndex: 1800,
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      selectedLocationIds.length === 0
                        ? t("teamMemberLinkingFormSelectLocationFirst")
                        : managersLoading
                        ? t("teamMemberLinkingFormLoadingManagers")
                        : t("teamMemberLinkingFormSelectManager")
                    }
                    variant="outlined"
                    size="small"
                    sx={{
                      bgcolor: "#E4E2E2",
                      borderRadius: 2,
                      mb: 0.5,
                    }}
                    error={!!errors.manager}
                  />
                )}
                renderTags={() => null}
                isOptionEqualToValue={(option, value) =>
                  option.id === value.id
                }
              />

              {/* Render selected managers as chips */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
                {field.value?.map((manager) => (
                  <Chip
                    key={manager.id}
                    label={manager.name}
                    onDelete={() =>
                      field.onChange(
                        field.value.filter((m) => m.id !== manager.id)
                      )
                    }
                    sx={{
                      bgcolor: "#EEDCFF",
                      color: "#6A0DAD",
                      fontWeight: 500,
                      textTransform: "lowercase",
                      "& .MuiChip-deleteIcon": {
                        color: "#8800F7",
                      },
                    }}
                  />
                ))}
              </Box>

              {errors.manager && (
                <FormHelperText sx={{ color: "red" }}>
                  {errors.manager.message}
                </FormHelperText>
              )}

              {managersError && (
                <FormHelperText sx={{ color: "red" }}>
                  {t("teamMemberLinkingFormFailedToLoadManagers")}
                </FormHelperText>
              )}
            </>
          )}
        />

        {/* Count and 'See all' link for managers */}
        {(watch_manager || [])?.length > 0 && (
          <Box
            sx={{ display: "flex", justifyContent: "space-between", my: 2 }}
          >
            <Typography sx={{ fontSize: 14, color: "#222" }}>
              {t("teamMemberLinkingFormSelectedCount", { count: (watch_manager || [])?.length })}
            </Typography>
            <Box onClick={hanldeOpenSelectedManagersDialog}>
              <Typography
                sx={{
                  fontSize: 14,
                  color: "#596AFF",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {t("teamMemberLinkingFormSeeAll")}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Working days/hours per location selected */}
        {watch_location?.length > 0 && (
          <Box m={5}>
            <Typography
              sx={{
                fontWeight: 700,
                color: "#232C3D",
                fontSize: 17,
                mt: 2,
                mb: 1,
              }}
            >
              {t("teamMemberLinkingFormWorkingDaysHoursPerLocation")}
            </Typography>

            <Box>
              {(watch_location || []).map((loc, index) => (
                <Box key={loc?._id} sx={{ mb: 2 }}>
                  {/* Location row with buttons */}
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <LocationOnIcon sx={{ color: "#5B24C9", mr: 1 }} />
                    <Typography
                      sx={{
                        color: "#5B24C9",
                        fontWeight: 500,
                        fontSize: 16,
                        flex: 1,
                      }}
                    >
                      {loc?.businessLocationName}
                      {loc?.permissions && Object.keys(loc.permissions).length > 0 && (
                        <Typography
                          component="span"
                          sx={{
                            ml: 1,
                            fontSize: 12,
                            color: "#4CAF50",
                            fontWeight: 600,
                          }}
                        >
                          ✓ permissions set
                        </Typography>
                      )}
                      {loc?.workingHours && Object.keys(loc.workingHours).length > 0 && (
                        <Typography
                          component="span"
                          sx={{
                            ml: 1,
                            fontSize: 12,
                            color: "#4CAF50",
                            fontWeight: 600,
                          }}
                        >
                          ✓ hours set
                        </Typography>
                      )}
                    </Typography>
                    <IconButton
                      onClick={() => {
                        setSelectedLocationIndex(index);
                        hanldeOpenPermissionDialog();
                      }}
                      sx={{
                        bgcolor: loc?.permissions && Object.keys(loc.permissions).length > 0 ? "#E8F5E8" : "#FFEBEE",
                        color: loc?.permissions && Object.keys(loc.permissions).length > 0 ? "#4CAF50" : "#F44336",
                        "&:hover": {
                          bgcolor: loc?.permissions && Object.keys(loc.permissions).length > 0 ? "#C8E6C9" : "#FFCDD2",
                        },
                      }}
                    >
                      <Tooltip title={loc?.permissions && Object.keys(loc.permissions).length > 0 ? t("teamMemberLinkingFormEditPermissions") : t("teamMemberLinkingFormSetPermissions")}>
                        <GroupIcon />
                      </Tooltip>
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        setSelectedLocationIndex(index);
                        hanldeOpenSelectWorkingHourDialog();
                      }}
                      sx={{
                        bgcolor: loc?.workingHours && Object.keys(loc.workingHours).length > 0 ? "#E8F5E8" : "#FFEBEE",
                        color: loc?.workingHours && Object.keys(loc.workingHours).length > 0 ? "#4CAF50" : "#F44336",
                        "&:hover": {
                          bgcolor: loc?.workingHours && Object.keys(loc.workingHours).length > 0 ? "#C8E6C9" : "#FFCDD2",
                        },
                      }}
                    >
                      <Tooltip title={loc?.workingHours && Object.keys(loc.workingHours).length > 0 ? t("teamMemberLinkingFormEditWorkingHours") : t("teamMemberLinkingFormSetWorkingHours")}>
                        <AccessTimeIcon />
                      </Tooltip>
                    </IconButton>
                  </Box>

                  {/* Errors for this specific location */}
                  {(() => {
                    const locationErrors = errors.location?.[index];
                    if (locationErrors) {
                      return (
                        <Box sx={{ ml: 4, mb: 1 }}>
                          {locationErrors.permissions && (
                            <FormHelperText sx={{ color: "#F44336", mb: 1 }}>
                              {locationErrors.permissions.message}
                            </FormHelperText>
                          )}
                          {locationErrors.workingHours && (
                            <FormHelperText sx={{ color: "#F44336" }}>
                              {locationErrors.workingHours.message}
                            </FormHelperText>
                          )}
                        </Box>
                      );
                    }
                    return null;
                  })()}
                </Box>
              ))}

              {(watch_location || []).length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Typography sx={{ fontSize: 14, color: "#222" }}>
                    {t("teamMemberLinkingFormSelectedCount", { count: (watch_location || [])?.length })}
                  </Typography>
                  <Box onClick={hanldeOpenSelectedLocationDialog}>
                    <Typography
                      sx={{
                        fontSize: 14,
                        color: "#596AFF",
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                    >
                      {t("teamMemberLinkingFormSeeAll")}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Button
          fullWidth
          variant="contained"
          type="submit"
          disabled={isSubmitting}
          sx={{
            bgcolor: "#8800F7",
            color: "#fff",
            borderRadius: 2,
            fontWeight: 700,
            fontSize: 18,
            textTransform: "none",
            minHeight: 46,
            boxShadow: "none",
            my: 3,
            "&:hover": { bgcolor: "#7600D3" },
            "&:disabled": {
              bgcolor: "#E0E0E0",
              color: "#757575",
            },
          }}
        >
          {isSubmitting ? t("teamMemberLinkingFormProcessing") : (isEditMode ? t("teamMemberLinkingFormUpdate") : t("teamMemberLinkingFormSend"))}
        </Button>
      </form>

      {/* Nested Dialogs */}
      <AllSelectedLocationsDialog
        open={openSelectedLocationDialog}
        onClose={hanldeOpenSelectedLocationDialog}
        locations={watch_location}
        setSelectedLocationIndex={setSelectedLocationIndex}
        hanldeOpenPermissionDialog={hanldeOpenPermissionDialog}
      />
      <PermissionsDialog
        setValue={setValue}
        open={openPermissionDialog}
        onClose={hanldeOpenPermissionDialog}
        permissions={watch_location[selectedLocationIndex]?.permissions || null}
        locationIndex={selectedLocationIndex}
        watch_location={watch_location}
        systemRole={watch_systemRole}
      />
      <SelectedLocationsDialog
        open={openSelectedLocationFirstDialog}
        onClose={hanldeOpenSelectedLocationFirstDialog}
        locations={watch_location}
        setValue={setValue}
      />
      <SelectedManagersDialog
        open={openSelectedManagersDialog}
        onClose={hanldeOpenSelectedManagersDialog}
        managers={watch_manager}
        setValue={setValue}
      />
      <WorkHoursDialog
        open={openSelectWorkingHourDialog}
        onClose={hanldeOpenSelectWorkingHourDialog}
        locationData={watch_location[selectedLocationIndex]}
        setValue={setValue}
        watch_location={watch_location}
        selectedLocationIndex={selectedLocationIndex}
        trigger={trigger}
      />
    </Box>
  );
}

export default TeamMemberLinkingForm;
